# Identifiers & references

The SEEK API provides a number of fields that can be used to identify a position.
They fall into two separate categories:

1. The [`PositionOpening.documentId`] and [`PositionProfile.profileId`] fields are unique [object identifiers] assigned by the SEEK API.

   While these are the primary way to identify positions in the SEEK API, they’re not available on the [SEEK Employer website].

2. Reference fields are optional strings that can be provided when [creating a position opening] or [posting a job ad].
   The SEEK API does not require that references are unique.
   Your software may reuse a reference across multiple job ads.

   Do not include any [personal information] such as a legal name or email address.
   Depending on the particular reference field, they can be used to correlate job ads posted via the SEEK API and the SEEK Employer website.

## Reference fields

### seekHirerJobReference

The `PositionProfile.seekHirerJobReference` field is a generic reference that can be set on a job ad and viewed across [all channels](#channel-support).
This makes it useful for supporting cross-channel workflows such as [exporting applications for jobs posted outside the SEEK API].
SEEK hirers typically use this as their primary reference due to its broad support.

[exporting applications for jobs posted outside the seek api]: /use-cases/application-export#job-ads-posted-outside-of-your-software

### seekBillingReference

The `PositionProfile.seekBillingReference` field is intended to contain a billing reference or cost centre code.
This field can be set on a job ad via the SEEK API, but is typically unavailable on the SEEK Employer website.
It appears on hirer invoices & contract reporting from SEEK so that costs incurred can be attributed to the relevant business unit or department.

### seekPartnerMetadata

The `PositionOpening.seekPartnerMetadata` & `PositionProfile.seekPartnerMetadata` fields are intended to contain metadata that’s useful to your software.
They can only be populated or retrieved via the SEEK API; they’re not directly exposed to SEEK hirers.

Note that SEEK hirers can have [relationships] with multiple integration partners or move between integration partners over time.
The [`PositionProfile.seekCreatedBySelfIndicator` field] can identify job ads posted by other integration partners so their metadata can be ignored.

[`positionprofile.seekcreatedbyselfindicator` field]: /schema/named-type/PostedPositionProfile#/field/seekCreatedBySelfIndicator
[relationships]: /auth/hirer-relationships

### seekPartnerPositionId

The `PostedPositionProfile.seekPartnerPositionId` field is a unique identifier that can be set on a job ad via the SEEK API.
It is used to uniquely identify a position in your software.
This field is only available on a [`PostedPositionProfile`] when [syncing positions].

[`PostedPositionProfile`]: /schema/named-type/PostedPositionProfile
[syncing positions]: /use-cases/job-posting/ad-sync/sync-positions

## Channel support

import { IconCaution, IconClear, IconTick, Text } from 'braid-design-system';

### Populated by

The table below shows which identifiers & references can be populated in each channel.

| Field                                         |           SEEK API           |     SEEK Employer website      |
| --------------------------------------------- | :--------------------------: | :----------------------------: |
| `PositionOpening.documentId`                  | <IconTick tone="positive" /> | <IconClear tone="critical" />  |
| `PositionOpening.seekPartnerMetadata`         | <IconTick tone="positive" /> | <IconClear tone="critical" />  |
| `PositionProfile.profileId`                   | <IconTick tone="positive" /> | <IconClear tone="critical" />  |
| `PositionProfile.seekHirerJobReference`       | <IconTick tone="positive" /> |  <IconTick tone="positive" />  |
| `PositionProfile.seekBillingReference`        | <IconTick tone="positive" /> | <IconCaution tone="caution" /> |
| `PositionProfile.seekPartnerMetadata`         | <IconTick tone="positive" /> | <IconClear tone="critical" />  |
| `PostedPositionProfile.seekPartnerPositionId` | <IconTick tone="positive" /> | <IconClear tone="critical" />  |

<Text>
  <IconCaution tone="caution" />
  &nbsp;&nbsp;Referred to as “agent job reference”. Only available when posting
  as an agency on behalf of a hirer.
</Text>

### Visible in

The table below shows which identifiers & references can be viewed in each channel.

| Field                                         |           SEEK API           |      SEEK Employer website      |                   Hirer billing & reports                    |
| --------------------------------------------- | :--------------------------: | :-----------------------------: | :----------------------------------------------------------: |
| `PositionOpening.documentId`                  | <IconTick tone="positive" /> |  <IconClear tone="critical" />  |                <IconClear tone="critical" />                 |
| `PositionOpening.seekPartnerMetadata`         | <IconTick tone="positive" /> |  <IconClear tone="critical" />  |                <IconClear tone="critical" />                 |
| `PositionProfile.profileId`                   | <IconTick tone="positive" /> |  <IconClear tone="critical" />  |                <IconClear tone="critical" />                 |
| `PositionProfile.seekHirerJobReference`       | <IconTick tone="positive" /> |  <IconTick tone="positive"  />  |  <p><IconTick tone="positive" /></p><p>Advertiser ad ID</p>  |
| `PositionProfile.seekBillingReference`        | <IconTick tone="positive" /> | <IconCaution tone="caution"  /> | <p><IconTick tone="positive"  /></p><p>Billing reference</p> |
| `PositionProfile.seekPartnerMetadata`         | <IconTick tone="positive" /> |  <IconClear tone="critical" />  |                <IconClear tone="critical" />                 |
| `PostedPositionProfile.seekPartnerPositionId` | <IconTick tone="positive" /> |  <IconClear tone="critical" />  |                <IconClear tone="critical" />                 |

<Text>
  <IconCaution tone="caution" />
  &nbsp;&nbsp;Referred to as “agent job reference”. Only available when posting
  as an agency on behalf of a hirer.
</Text>

[`positionopening.documentid`]: /schema/named-type/PositionOpening#/field/documentId
[`positionprofile.profileid`]: /schema/named-type/PositionProfile#/field/profileId
[creating a position opening]: /use-cases/job-posting/position-openings
[object identifiers]: /graphql/seek-api-conventions#object-identifiers
[personal information]: https://www.oaic.gov.au/privacy/privacy-guidance-for-organisations-and-government-agencies/handling-personal-information/what-is-personal-information
[posting a job ad]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad
[seek employer website]: https://employer.seek.com
